/**
 * Storage utility for managing authentication and application data
 * Uses sessionStorage for authentication tokens (tab-specific)
 * Uses localStorage for non-auth preferences (shared across tabs)
 */

// Authentication-related keys that should use sessionStorage
const AUTH_KEYS = [
  'adminToken',
  'workerToken',
  'customerToken',
  'donorToken',
  'admin_id',
  'worker_id',
  'CustomerId',
  'DonorId',
  'CompanyId',
  'CompanyUrl',
  'isImpersonated',
  'impersonatedBy',
  'oldSession',
  'oldAdminId',
  'appUrl',
  'currentLogoPath',
];

/**
 * Get the appropriate storage based on key type
 * @param {string} key - Storage key
 * @returns {Storage} - sessionStorage for auth keys, localStorage for others
 */
const getStorage = (key) => {
  return AUTH_KEYS.includes(key) ? sessionStorage : localStorage;
};

/**
 * Get item from storage
 * @param {string} key - Storage key
 * @returns {string|null} - Stored value or null
 */
export const getItem = (key) => {
  try {
    const storage = getStorage(key);
    return storage.getItem(key);
  } catch (error) {
    console.error(`Error getting item ${key}:`, error);
    return null;
  }
};

/**
 * Set item in storage
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
export const setItem = (key, value) => {
  try {
    if (key === null || key === undefined) {
      console.warn(`Cannot set item: key is null or undefined`);
      return;
    }
    if (value === null || value === undefined) {
      console.warn(`Cannot set item ${key}: value is null or undefined`);
      return;
    }
    const storage = getStorage(key);
    const stringValue = String(value); // Convert to string
    storage.setItem(key, stringValue);
    // Verify it was set correctly
    const retrieved = storage.getItem(key);
    if (retrieved !== stringValue) {
      console.error(`Failed to set item ${key}: value mismatch`);
    }
  } catch (error) {
    console.error(`Error setting item ${key}:`, error);
    // Fallback to localStorage if sessionStorage fails (e.g., in private browsing)
    try {
      if (AUTH_KEYS.includes(key)) {
        localStorage.setItem(key, String(value));
        console.warn(`Fell back to localStorage for ${key} due to sessionStorage error`);
      }
    } catch (fallbackError) {
      console.error(`Fallback storage also failed for ${key}:`, fallbackError);
    }
  }
};

/**
 * Remove item from storage
 * @param {string} key - Storage key
 */
export const removeItem = (key) => {
  try {
    const storage = getStorage(key);
    storage.removeItem(key);
  } catch (error) {
    console.error(`Error removing item ${key}:`, error);
  }
};

/**
 * Clear all items from storage
 * @param {boolean} clearAuthOnly - If true, only clear auth keys. If false, clear everything
 */
export const clear = (clearAuthOnly = false) => {
  try {
    if (clearAuthOnly) {
      // Clear only auth keys from sessionStorage
      AUTH_KEYS.forEach((key) => {
        sessionStorage.removeItem(key);
      });
    } else {
      // Clear all sessionStorage and localStorage
      sessionStorage.clear();
      localStorage.clear();
    }
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

/**
 * Clear all authentication-related items
 */
export const clearAuth = () => {
  try {
    AUTH_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key); // Also clear from localStorage in case of migration
    });
  } catch (error) {
    console.error('Error clearing auth storage:', error);
  }
};

/**
 * Check if a key exists in storage
 * @param {string} key - Storage key
 * @returns {boolean} - True if key exists
 */
export const hasItem = (key) => {
  try {
    const storage = getStorage(key);
    return storage.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking item ${key}:`, error);
    return false;
  }
};

// Export default object for convenience
export default {
  getItem,
  setItem,
  removeItem,
  clear,
  clearAuth,
  hasItem,
};

